import React, { useState, useEffect } from 'react';
import { BrickCharts, ExportManager } from 'brickcharts/dist/brickcharts.es.js';

function ChartExporter({ chartType = 'hot-100' }) {
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const exportFormats = [
    { value: 'csv', label: 'CSV', icon: '📊', description: 'Comma-separated values for spreadsheet analysis' },
    { value: 'json', label: 'JSON', icon: '📄', description: 'Structured data format for developers' },
    { value: 'svg', label: 'SVG Chart', icon: '📈', description: 'Scalable vector graphics chart visualization' },
    { value: 'xlsx', label: 'Excel', icon: '📋', description: 'Microsoft Excel spreadsheet format' },
    { value: 'pdf', label: 'PDF', icon: '📑', description: 'Portable document format for reports' }
  ];

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const brickCharts = new BrickCharts({
          enableCache: true
        });

        const chart = await brickCharts.getChart(chartType);
        setChartData(chart);
      } catch (error) {
        setError(error.message);
        console.error('Chart data fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [chartType]);

  const handleExport = async (format) => {
    if (!chartData) {
      setExportResult({ success: false, error: 'No chart data available' });
      return;
    }

    try {
      setExporting(true);
      setExportResult(null);

      const exportManager = new ExportManager();
      const result = await exportManager.exportChartData(chartData, {
        format,
        filename: `${chartType}-chart-${Date.now()}.${format}`,
        includeMetadata: true,
        includeStats: true
      });

      if (result.success) {
        // Create download link
        const blob = new Blob([result.data], { 
          type: getMimeType(format) 
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.filename || `chart.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setExportResult({ success: true, format, filename: result.filename });
      } else {
        setExportResult({ success: false, error: result.error });
      }
    } catch (error) {
      setExportResult({ success: false, error: error.message });
    } finally {
      setExporting(false);
    }
  };

  const getMimeType = (format) => {
    switch (format) {
      case 'csv': return 'text/csv';
      case 'json': return 'application/json';
      case 'svg': return 'image/svg+xml';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'pdf': return 'application/pdf';
      default: return 'text/plain';
    }
  };

  const getExportStats = () => {
    if (!chartData) return null;

    return {
      totalEntries: chartData.entries?.length || 0,
      newEntries: chartData.entries?.filter(entry => !entry.lastWeek).length || 0,
      chartType: chartData.chartType || chartType,
      date: chartData.date ? new Date(chartData.date).toLocaleDateString() : 'Unknown',
      topArtist: chartData.entries?.[0]?.artist || 'N/A',
      topSong: chartData.entries?.[0]?.title || 'N/A'
    };
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading chart data for export...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <h3>Error Loading Chart Data</h3>
        <p>{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="retry-btn"
        >
          Try Again
        </button>
      </div>
    );
  }

  const exportStats = getExportStats();

  return (
    <div className="chart-exporter">
      <div className="export-header">
        <h3>📤 Export Chart Data</h3>
        <p>Download chart data in various formats for analysis and reporting</p>
      </div>

      {exportStats && (
        <div className="export-stats">
          <div className="stats-grid">
            <div className="stat">
              <span className="stat-label">Chart Type</span>
              <span className="stat-value">{exportStats.chartType}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Total Entries</span>
              <span className="stat-value">{exportStats.totalEntries}</span>
            </div>
            <div className="stat">
              <span className="stat-label">New Entries</span>
              <span className="stat-value">{exportStats.newEntries}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Chart Date</span>
              <span className="stat-value">{exportStats.date}</span>
            </div>
            <div className="stat">
              <span className="stat-label">#1 Artist</span>
              <span className="stat-value">{exportStats.topArtist}</span>
            </div>
            <div className="stat">
              <span className="stat-label">#1 Song</span>
              <span className="stat-value">{exportStats.topSong}</span>
            </div>
          </div>
        </div>
      )}

      <div className="export-formats">
        <h4>Available Export Formats</h4>
        <div className="format-grid">
          {exportFormats.map(format => (
            <div key={format.value} className="format-card">
              <div className="format-icon">{format.icon}</div>
              <div className="format-info">
                <h5>{format.label}</h5>
                <p>{format.description}</p>
              </div>
              <button
                onClick={() => handleExport(format.value)}
                disabled={exporting || !chartData}
                className="export-btn"
              >
                {exporting ? 'Exporting...' : `Export as ${format.label}`}
              </button>
            </div>
          ))}
        </div>
      </div>

      {exportResult && (
        <div className={`export-result ${exportResult.success ? 'success' : 'error'}`}>
          {exportResult.success ? (
            <div>
              <span>✅ Successfully exported as {exportResult.format.toUpperCase()}</span>
              {exportResult.filename && (
                <p className="filename">File: {exportResult.filename}</p>
              )}
            </div>
          ) : (
            <div>
              <span>❌ Export failed: {exportResult.error}</span>
              <p className="error-tip">
                Try refreshing the page or selecting a different format.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="export-tips">
        <h4>💡 Export Tips</h4>
        <ul>
          <li><strong>CSV:</strong> Best for spreadsheet analysis and data processing</li>
          <li><strong>JSON:</strong> Ideal for developers and API integration</li>
          <li><strong>SVG:</strong> Perfect for web graphics and presentations</li>
          <li><strong>Excel:</strong> Great for business reports and presentations</li>
          <li><strong>PDF:</strong> Best for formal reports and documentation</li>
        </ul>
      </div>

      {!chartData && (
        <div className="no-data">
          <p>No chart data available for export</p>
          <p>Please ensure the chart data is loaded before attempting to export.</p>
        </div>
      )}
    </div>
  );
}

export default ChartExporter; 